import { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, CheckCircle } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Public reset-password screen. Reads the token from the URL query string
 * (`/reset-password?token=xxx`), validates the new password matches +
 * meets the length policy, then POSTs to `/auth/reset-password`.
 */
export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("rpNoToken"));
      return;
    }
    if (pwd !== confirm) {
      toast.error(t("cpMismatch"));
      return;
    }
    if (pwd.length < 8) {
      toast.error(t("cpTooShort"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md w-full bg-white border rounded-lg p-8 text-center">
          <p className="font-black text-slate-900 mb-2">{t("rpNoToken")}</p>
          <Link to="/forgot-password" className="text-blue-900 text-sm">{t("rpRequestNew")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.08)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-md bg-blue-900 flex items-center justify-center">
            <HardHat size={22} weight="fill" className="text-amber-400" />
          </div>
          <div className="leading-tight">
            <h1 className="font-heading font-black text-lg tracking-tight text-slate-900">{t("rpTitle")}</h1>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">UG Managment</p>
          </div>
        </div>

        {done ? (
          <div data-testid="rp-success" className="space-y-4">
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold mb-1">{t("rpSuccess")}</p>
                <p>{t("rpSuccessBody")}</p>
              </div>
            </div>
            <Button data-testid="rp-goto-login" onClick={() => navigate("/login")}
                     className="w-full bg-blue-900 hover:bg-blue-800">
              {t("rpGoLogin")}
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6">{t("rpBlurb")}</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label className="overline-label">{t("cpNew")}</Label>
                <Input data-testid="rp-new" type="password" required minLength={8} autoComplete="new-password"
                        value={pwd} onChange={(e) => setPwd(e.target.value)} />
                <p className="text-[10px] text-slate-500">{t("cpHint")}</p>
              </div>
              <div className="space-y-2">
                <Label className="overline-label">{t("cpConfirm")}</Label>
                <Input data-testid="rp-confirm" type="password" required minLength={8} autoComplete="new-password"
                        value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <Button data-testid="rp-submit" type="submit" disabled={busy}
                       className="w-full bg-blue-900 hover:bg-blue-800 font-semibold">
                {busy ? "..." : t("rpSubmit")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
